// The theme sentiment indicator (docs/phase-0/slice6-component-designs.md §5.1
// `SentimentIndicator`; UX §5.1). Icon+text always, never color alone
// (`color-not-only`): a small frown/neutral/smile glyph paired with the plain word.
// `sentiment` is nullable until a theme has enough rated submissions to aggregate
// (`FeedbackTheme.sentiment`, feedback.ts:60) - renders "Not enough ratings yet" in
// `.muted` with NO icon and no default tone, never a silent "Neutral" guess.
import type { Sentiment } from "@nightcrew/shared";
import { feedbackCopy } from "../copy/feedback.js";

const SENTIMENT_COPY_KEY = {
  pos: "feedback.themes.sentiment.pos",
  neu: "feedback.themes.sentiment.neu",
  neg: "feedback.themes.sentiment.neg",
} as const;

/** One glyph per sentiment - a plain mouth-curve shape, aria-hidden (never the sole
 * signal). Distinct paths so the three are visually distinguishable at a glance, not
 * just by adjacent text. */
const SentimentGlyph = ({ sentiment }: { readonly sentiment: Sentiment }): React.JSX.Element => {
  const mouthPath =
    sentiment === "pos" ? "M8 14c1.2 1.4 2.8 1.4 4 0" : sentiment === "neg" ? "M8 15.4c1.2-1.4 2.8-1.4 4 0" : "M8 14.7h4";
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="9" cy="10" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="0.75" fill="currentColor" stroke="none" />
      <path d={mouthPath} strokeLinecap="round" transform="translate(0,-1)" />
    </svg>
  );
};

export const FeedbackSentimentIndicator = ({
  sentiment,
}: {
  readonly sentiment: Sentiment | null;
}): React.JSX.Element => {
  if (sentiment === null) {
    return (
      <span className="muted sentiment-indicator" data-testid="sentiment-unknown" style={{ fontSize: 13 }}>
        {feedbackCopy("feedback.themes.sentiment.unknown")}
      </span>
    );
  }
  return (
    <span className="sentiment-indicator" data-testid={`sentiment-${sentiment}`}>
      <SentimentGlyph sentiment={sentiment} />
      {feedbackCopy(SENTIMENT_COPY_KEY[sentiment])}
    </span>
  );
};
